import { memo } from 'react'
import type { BoggleGameState } from '~/boggle/engine/standard/types'
import type { BogglePlaygroundMeterSettings } from '~/boggle/config/playground'
import { BOGGLE_GOLD_RUSH_BURST_ID } from '~/boggle/lib/meterBursts'
import { MeterGauge } from '~/boggle/ui/run/MeterGauge'
import classes from '~/boggle/ui/run/RunHud.module.css'
import {
  useBoggleIsInOvertime,
  useBoggleMeterAtOrAboveCapacity,
  useBoggleMeterGoldRushActive,
  type BoggleStore,
} from '~/boggle/ui/state/Store'

/** Meter gauge and layout placeholder — sole live meter subscription boundary for the HUD row. */
export const RunMeterIsland = memo(function RunMeterIsland({
  bufferedRunState,
  meterHudPhasePlaying,
  meterSettings,
  showEndBuffer,
  showMeterCounter,
  showMeterGauge,
  store,
}: {
  bufferedRunState: BoggleGameState
  meterHudPhasePlaying: boolean
  meterSettings: BogglePlaygroundMeterSettings
  showEndBuffer: boolean
  showMeterCounter: boolean
  showMeterGauge: boolean
  store: BoggleStore
}) {
  if (showMeterGauge) {
    if (showEndBuffer) {
      const tileSpawnGlowActive =
        meterSettings.tileSpawn.enabled &&
        bufferedRunState.meter.value >= meterSettings.capacity &&
        !(
          bufferedRunState.meter.burstActive &&
          bufferedRunState.meter.activeBurstId === BOGGLE_GOLD_RUSH_BURST_ID
        ) &&
        !bufferedRunState.isInOvertime &&
        meterHudPhasePlaying

      return (
        <MeterGauge
          diameter={120}
          fontMetricsDiameter={122}
          tileSpawnGlowActive={tileSpawnGlowActive}
          burstActive={
            bufferedRunState.meter.burstActive &&
            bufferedRunState.meter.activeBurstId === BOGGLE_GOLD_RUSH_BURST_ID
          }
          overmeterValue={bufferedRunState.meter.overmeterValue}
          progressMeterWidth={12}
          settings={meterSettings}
          value={bufferedRunState.meter.value}
        />
      )
    }

    return (
      <LiveMeterGaugeSlot
        meterHudPhasePlaying={meterHudPhasePlaying}
        meterSettings={meterSettings}
        store={store}
      />
    )
  }

  if (showMeterCounter) {
    return <div aria-hidden className={classes.gaugePlaceholder} />
  }

  return null
})

const LiveMeterGaugeSlot = memo(function LiveMeterGaugeSlot({
  meterHudPhasePlaying,
  meterSettings,
  store,
}: {
  meterHudPhasePlaying: boolean
  meterSettings: BogglePlaygroundMeterSettings
  store: BoggleStore
}) {
  const burstActive = useBoggleMeterGoldRushActive(store)
  const isInOvertime = useBoggleIsInOvertime(store)
  const atOrAboveCapacity = useBoggleMeterAtOrAboveCapacity(store, meterSettings.capacity)
  const tileSpawnGlowActive =
    meterSettings.tileSpawn.enabled &&
    atOrAboveCapacity &&
    !burstActive &&
    !isInOvertime &&
    meterHudPhasePlaying

  return (
    <MeterGauge
      diameter={120}
      fontMetricsDiameter={122}
      tileSpawnGlowActive={tileSpawnGlowActive}
      burstActive={burstActive}
      progressMeterWidth={12}
      settings={meterSettings}
      store={store}
    />
  )
})
